import React, { createContext, FC, useCallback, useContext, useState } from 'react';

export interface StepStatus {
    isRequired: boolean;
    isCompleted: boolean;
}

export interface Steps {
    label: string;
    content: React.ReactNode;
}

interface StepperContextValue {
    activeStep: number;
    goToStep: (step: number) => void;
    isFirstStep: boolean;
    isLastStep: boolean;
    nextStep: () => void;
    previousStep: () => void;
    stepCount: number;
    stepLabels: string[];
    steps: Steps[];
    stepStatuses: StepStatus[];
    updateStepStatus: (stepIndex: number, status: StepStatus) => void;
}

const StepperContext = createContext<StepperContextValue | undefined>(undefined);

export const StepperProvider: FC<{ children: React.ReactNode; steps: Steps[] }> = ({ children, steps }) => {
    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const stepCount = steps.length;
    const stepLabels = steps.map((step) => step.label);

    const [stepStatuses, setStepStatuses] = useState<StepStatus[]>(() =>
        stepLabels.map(() => ({ isRequired: false, isCompleted: false }))
    );

    const updateStepStatus = useCallback((stepIndex: number, stepStatus: StepStatus) => {
        setStepStatuses((prev) => prev.map((step, index) => (index === stepIndex ? { ...step, ...stepStatus } : step)));
    }, []);

    const nextStep = useCallback(() => {
        setCurrentStepIndex((stepIndex) => {
            const nextStep = stepIndex + 1;
            if (nextStep < stepCount) {
                return nextStep;
            }
            return stepIndex;
        });
    }, [stepCount]);

    const previousStep = useCallback(() => {
        setCurrentStepIndex((stepIndex) => {
            const prevStep = stepIndex - 1;
            if (prevStep >= 0) {
                return prevStep;
            }
            return stepIndex;
        });
    }, []);

    const goToStep = useCallback(
        (stepIndex: number) => {
            setCurrentStepIndex(stepIndex);
        },
        [stepCount]
    );

    const value: StepperContextValue = {
        activeStep: currentStepIndex,
        goToStep,
        isFirstStep: currentStepIndex === 0,
        isLastStep: currentStepIndex === stepCount - 1,
        nextStep,
        previousStep,
        stepCount,
        stepLabels,
        steps,
        stepStatuses,
        updateStepStatus,
    };

    return <StepperContext.Provider value={value}>{children}</StepperContext.Provider>;
};

export const useStepper = (): StepperContextValue => {
    const context = useContext(StepperContext);
    if (context === undefined) {
        throw new Error('useStepper must be used within a StepperProvider');
    }
    return context;
};
